// KPI-ASSIGN (review build item #7): the one KPI assignment editor. Giving a seat a KPI used to
// take four screens (define it in Metrics, set its goal in Targets, assign it in Seat weights,
// and Role defaults); here it is one dialog, opened from every place a seat is shown (the seat
// page's KPIs tab, the DE profile's KPIs tab, the chart panel's "Add a measurable"):
//   1. the measurable: pick a company metric, or create one (by hand, CSV, or from events with
//      the event builder);
//   2. how it is judged: "good when" (direction) and cadence, which belong to the metric (a
//      change says it applies on every seat that uses it);
//   3. the seat's target (a seat rule; the company rule stays the fallback);
//   4. scoring: Scored / Context / Off, the KPI weight, penalty only (goal weight under it).
// Saving writes, in order: the metric (POST, or PATCH when direction or cadence changed), the
// seat's row alone (`PUT seats/{id}/metrics/{metric_id}`, so no other KPI of the seat is
// rewritten), then the target rule (POST, PATCH, or ended when cleared). A failed step says
// which; saving again resumes (a created metric is then picked).
// TEAM-KPI (founder-approved 2026-10-21): "Assign to: Seat / Team". A team KPI (opened from a
// team, or switched to from a seat) takes the team's target and where its value comes from —
// the team's own value, or its member seats' added or averaged — and saves through
// `PUT teams/{id}/metrics/{metric_id}` (the target in the same call). Both modes end with the
// metric's warning bands (bands-editor.tsx, with a live preview); changed bands are written on
// the metric (PATCH, or in the POST of a new one), so they apply wherever it is used.

import { TEAM_ROLLUP_LABELS, type TeamRollup } from "@companyos/domain-kpi";
import { ApiError } from "@companyos/sdk";
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  NativeSelect,
  NativeSelectOption,
} from "@companyos/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CheckIcon, SearchIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type { MetricDef, SeatMetric, TargetRule } from "../../api/types";
import { currentWeek, isoDate, weekStart } from "../../focus/weeks";
import { teamsQuery } from "../chart/chart-queries";
import { type CompanyAccess, isManager } from "../company";
import { companyQuery, keys, seatMetricSetQuery } from "../queries";
import {
  CADENCE_OPTIONS,
  DIRECTION_OPTIONS,
  labelOf,
  PERIOD_OPTIONS,
  toInputValue,
  toStoredValue,
  UNIT_HINT,
  UNIT_OPTIONS,
} from "../structure/labels";
import { FormError } from "../structure/parts";
import {
  BandsEditor,
  type BandsForm,
  bandsChanged,
  bandsFormOf,
  bandsOfForm,
} from "./bands-editor";
import {
  blankQuery,
  describeQuery,
  EventQueryBuilder,
  type QueryForm,
  queryOfForm,
} from "./event-query";
import { useAllMetrics } from "./queries";
import { type TeamMetricLine, teamMetricsQuery } from "./team-queries";

type Period = Exclude<TargetRule["period_type"], "round">;
type Tracking = SeatMetric["tracking_mode"];

const PERIOD_OF: Record<NonNullable<MetricDef["cadence"]>, Period> = {
  daily: "week",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

const TRACKING: { value: Tracking; label: string; hint: string }[] = [
  { value: "scored", label: "Scored", hint: "Counts toward the seat's KPI score." },
  { value: "context", label: "Context", hint: "Shown on the scorecard, never scored." },
  { value: "off", label: "Off", hint: "Kept on the seat, hidden from the scorecard." },
];

const WEIGHT = /^\d{1,3}(\.\d{1,3})?$/;

/** "Placements made" → `kpi.placements_made` (a free key, suffixed when taken). */
export function keyFor(name: string, taken: ReadonlySet<string>): string {
  const slug =
    name
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^(\d)/, "m_$1")
      .slice(0, 48) || "metric";
  let key = `kpi.${slug}`;
  for (let i = 2; taken.has(key); i++) key = `kpi.${slug}_${i}`;
  return key;
}

export type KpiAssignTarget = {
  seatId: string;
  /** "Senior Recruiter" */
  seatTitle: string;
  /** "held by Priya Shah" / "held by Riley (digital employee)" / null for an open seat. */
  holderText: string | null;
  /** True when the holder is a digital employee (an event metric reads "agent events"). */
  agent?: boolean;
};

const ROLLUPS: TeamRollup[] = ["none", "sum", "average"];

export function KpiAssignDialog({
  api,
  company,
  seat,
  team,
  onSeat,
  editing,
  editingTeam,
  asGoal = false,
  onClose,
  onSaved,
}: {
  api: Api;
  company: CompanyAccess;
  /** The seat it opens on (absent: opened from a team). */
  seat?: KpiAssignTarget;
  /** TEAM-KPI: the team it opens on (Team mode, the team fixed). */
  team?: { id: string; name: string };
  /** The seat's stored set (a metric already on it is not offered again). */
  onSeat: readonly SeatMetric[];
  /** Edit this row of the set (with its seat target, if any); absent = add a KPI. */
  editing?: { row: SeatMetric; target: TargetRule | null };
  /** TEAM-KPI: edit this KPI of `team` (its roll-up, weight and team target). */
  editingTeam?: { line: TeamMetricLine };
  /** SEAT-DRAWER-UX §2.2: a new seat KPI added as a goal (goal weight 1, KPI weight 0). */
  asGoal?: boolean;
  onClose: () => void;
  /** After a save, before the dialog closes (MODALS: a quick add's toast). */
  onSaved?: () => void;
}) {
  const qc = useQueryClient();
  const co = company.id;
  const metrics = useAllMetrics(api, co);
  const detail = useQuery(companyQuery(api, co));
  const currency = detail.data?.reporting_currency ?? "USD";
  const editId = editing?.row.metric_def_id ?? editingTeam?.line.metric_def_id;
  const editDef = editId ? metrics.all.find((m) => m.id === editId) : undefined;

  // TEAM-KPI: where the KPI goes. A dialog opened on a team (or editing a team KPI) stays there;
  // one opened on a seat may switch to a team the caller may edit (its lead, or a manager).
  const fixedTeam = team ?? null;
  const [assignTo, setAssignTo] = useState<"seat" | "team">(
    !seat || fixedTeam || editingTeam ? "team" : "seat",
  );
  const toTeam = assignTo === "team";
  const [teamId, setTeamId] = useState<string>(fixedTeam?.id ?? editingTeam?.line.team_id ?? "");
  const pickable = !fixedTeam && !editingTeam && !editing && Boolean(seat);
  const teams = useInfiniteQuery({ ...teamsQuery(api, co), enabled: pickable && toTeam });
  const allTeams = teams.data?.pages.flatMap((p) => p.data) ?? [];
  const sets = useQueries({
    queries: allTeams.map((t) => ({
      ...teamMetricsQuery(api, co, t.id),
      enabled: pickable && toTeam && !isManager(company),
    })),
  });
  const editableTeams = isManager(company)
    ? allTeams
    : allTeams.filter((_, i) => sets[i]?.data?.can_edit === true);
  const teamName =
    fixedTeam?.name ?? allTeams.find((t) => t.id === teamId)?.name ?? (teamId ? "the team" : null);
  const teamSet = useQuery({
    ...teamMetricsQuery(api, co, teamId),
    enabled: toTeam && Boolean(teamId),
  });
  const tt = editingTeam?.line.target ?? null;
  const [rollup, setRollup] = useState<TeamRollup>(editingTeam?.line.rollup ?? "sum");

  const [mode, setMode] = useState<"pick" | "create">("pick");
  const [picked, setPicked] = useState<string>(editId ?? "");
  const [find, setFind] = useState("");
  // A new metric.
  const [name, setName] = useState("");
  const [unit, setUnit] = useState<MetricDef["unit"]>("number");
  const [source, setSource] = useState<"manual" | "import" | "event">("manual");
  const [query, setQuery] = useState<QueryForm>(blankQuery);
  // The metric's judgement (null = as the picked metric has it).
  const [direction, setDirection] = useState<MetricDef["direction"] | null>(null);
  const [cadence, setCadence] = useState<NonNullable<MetricDef["cadence"]> | null>(null);
  // The seat's target and scoring.
  const t = editing?.target ?? null;
  const [targetText, setTargetText] = useState<string | null>(null);
  const [maxText, setMaxText] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period | null>(
    t ? (t.period_type as Period) : tt ? (tt.period_type as Period) : null,
  );
  const [tracking, setTracking] = useState<Tracking>(editing?.row.tracking_mode ?? "scored");
  const [kpiWeight, setKpiWeight] = useState(
    String(Number(editing?.row.kpi_weight ?? editingTeam?.line.kpi_weight ?? (asGoal ? 0 : 1))),
  );
  const [goalWeight, setGoalWeight] = useState(
    String(Number(editing?.row.goal_weight ?? (asGoal ? 1 : 0))),
  );
  const [penalty, setPenalty] = useState(editing?.row.penalty_only ?? false);
  // A metric created by an earlier, partly failed save (saving again picks it).
  const [created, setCreated] = useState<MetricDef | null>(null);
  // The metric's warning bands (null: as the picked metric has them).
  const [bandsForm, setBandsForm] = useState<BandsForm | null>(null);

  const onSeatIds = new Set(
    toTeam
      ? (teamSet.data?.data ?? []).map((l) => l.metric_def_id)
      : onSeat.map((r) => r.metric_def_id),
  );
  const available = metrics.all.filter(
    (m) =>
      !m.archived_at &&
      (!onSeatIds.has(m.id) || m.id === editId) &&
      // A team KPI needs a cadence (its periods).
      (!toTeam || m.cadence !== null),
  );
  const def: MetricDef | undefined =
    created ?? (mode === "pick" ? available.find((m) => m.id === picked) : undefined);
  const creating = mode === "create" && !created;
  const unitNow = creating ? unit : (def?.unit ?? "number");
  const dirNow = direction ?? def?.direction ?? "gte";
  const cadNow = cadence ?? def?.cadence ?? "weekly";
  const periodNow: Period = period ?? PERIOD_OF[cadNow] ?? "week";
  const prior = t
    ? { value: Number(t.value), max: t.value_max === null ? null : Number(t.value_max) }
    : tt
      ? { value: tt.value, max: tt.value_max }
      : null;
  const targetNow =
    targetText ?? (prior && editDef ? toInputValue(prior.value, editDef.unit, currency) : "");
  const maxNow =
    maxText ??
    (prior?.max !== null && prior?.max !== undefined && editDef
      ? toInputValue(prior.max, editDef.unit, currency)
      : "");
  const bandsNow: BandsForm = bandsForm ?? bandsFormOf(creating ? null : def?.bands);
  const bandsParsed = bandsOfForm(bandsNow);
  const built = queryOfForm(query);
  const storedTarget = targetNow.trim() ? toStoredValue(targetNow, unitNow, currency) : null;
  const storedMax =
    dirNow === "between" && maxNow.trim() ? toStoredValue(maxNow, unitNow, currency) : null;

  const problems: string[] = [];
  if (creating) {
    if (!name.trim()) problems.push("Name the new metric.");
    if (source === "event" && "error" in built) problems.push(built.error);
  } else if (!def) problems.push(metrics.isPending ? "Loading metrics…" : "Pick a metric.");
  if (targetNow.trim() && storedTarget === null) problems.push("The target is a number.");
  if (dirNow === "between" && targetNow.trim() && storedMax === null)
    problems.push("A range target needs its upper end.");
  if (!WEIGHT.test(kpiWeight) || (!toTeam && !WEIGHT.test(goalWeight)))
    problems.push("Weights are numbers from 0 to 999.999.");
  if ("error" in bandsParsed) problems.push(bandsParsed.error);
  if (toTeam && !teamId) problems.push("Pick a team.");
  if (!toTeam && !seat) problems.push("Pick a seat.");
  const newBands = "bands" in bandsParsed ? bandsParsed.bands : null;
  const bandsMoved =
    !creating && def !== undefined && "bands" in bandsParsed && bandsChanged(def.bands, newBands);
  const defChanged =
    !creating &&
    def !== undefined &&
    (dirNow !== def.direction || (def.cadence ?? null) !== (cadence ?? def.cadence ?? null));

  const save = useMutation({
    mutationFn: async () => {
      // 1. The metric.
      let metric = def;
      if (creating) {
        const taken = new Set(metrics.all.map((m) => m.key));
        metric = await api.client.metrics.create({
          params: { co },
          body: {
            key: keyFor(name, taken),
            name: name.trim(),
            unit,
            direction: dirNow,
            aggregation: unit === "percent" || unit === "rating" ? "average" : "cumulative",
            cadence: cadNow,
            source,
            ...(source === "event" && "query" in built ? { event_query: built.query } : {}),
            ...(newBands ? { bands: newBands } : {}),
          } as never,
        });
        setCreated(metric);
      } else if (metric && (defChanged || bandsMoved)) {
        metric = await api.client.metrics.update({
          params: { co, id: metric.id },
          body: {
            ...(defChanged ? { direction: dirNow, cadence: cadNow } : {}),
            ...(bandsMoved ? { bands: newBands } : {}),
          },
        });
      }
      if (!metric) throw new Error("No metric picked.");
      // TEAM-KPI: a team's row, its roll-up and its target in one call.
      if (toTeam) {
        await api.client.teams.putMetric({
          params: { co, id: teamId, metric_id: metric.id },
          body: {
            rollup,
            kpi_weight: kpiWeight,
            target:
              storedTarget === null
                ? null
                : {
                    period_type: periodNow,
                    value: String(storedTarget),
                    value_max: storedMax === null ? null : String(storedMax),
                  },
          },
        });
        return metric;
      }
      if (!seat) throw new Error("No seat picked.");
      // 2. The seat's row, alone, under the set's version (the one this seat's KPIs were shown
      // from; fetched only when nothing showed them), so a whole-set save made since is not lost.
      const shown = await qc.ensureQueryData(seatMetricSetQuery(api, co, seat.seatId));
      const saved = await api.client.seats.putMetric({
        params: { co, id: seat.seatId, metric_id: metric.id },
        ifMatch: shown.version,
        body: {
          kpi_weight: kpiWeight,
          goal_weight: goalWeight,
          tracking_mode: tracking,
          penalty_only: penalty,
        },
      });
      qc.setQueryData(keys.seatMetricSet(co, seat.seatId), saved);
      // 3. The seat's target.
      const from = isoDate(weekStart(currentWeek()));
      const old = editing?.target ?? null;
      if (storedTarget !== null) {
        const value = String(storedTarget);
        const value_max = storedMax === null ? null : String(storedMax);
        if (old && old.period_type === periodNow) {
          if (
            Number(old.value) !== storedTarget ||
            (old.value_max === null ? null : Number(old.value_max)) !== storedMax
          )
            await api.client.targets.update({
              params: { co, id: old.id },
              body: { value, value_max } as never,
            });
        } else {
          if (old) await endTarget(api, co, old, from);
          await api.client.targets.create({
            params: { co },
            body: {
              metric_def_id: metric.id,
              subject_kind: "seat",
              subject_id: seat.seatId,
              period_type: periodNow,
              value,
              value_max,
              effective_from: from,
              effective_to: null,
            } as never,
          });
        }
      } else if (old) await endTarget(api, co, old, from);
      return metric;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["company", co] });
      void qc.invalidateQueries({ queryKey: keys.metrics(co) });
      onSaved?.();
      onClose();
    },
    // A set changed since it was shown (409): reload it, so saving again writes over what is there.
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409 && seat)
        void qc.invalidateQueries({ queryKey: keys.seatMetricSet(co, seat.seatId) });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!problems.length) save.mutate();
  };

  const q = find.trim().toLowerCase();
  const shown = available.filter(
    (m) => !q || m.name.toLowerCase().includes(q) || m.key.toLowerCase().includes(q),
  );
  const title =
    editing || editingTeam
      ? `Edit ${editDef?.name ?? "KPI"}`
      : toTeam
        ? `Add a KPI to ${teamName ?? "a team"}`
        : `Add a KPI to ${seat?.seatTitle ?? "a seat"}`;
  const agent = !toTeam && seat?.agent;

  return (
    <Dialog open onOpenChange={(open) => !open && !save.isPending && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[680px]">
        <form
          onSubmit={onSubmit}
          noValidate
          className="flex flex-col gap-5"
          aria-labelledby="kpi-assign-title"
          data-slot="kpi-assign-dialog"
        >
          <DialogHeader>
            <DialogTitle id="kpi-assign-title">{title}</DialogTitle>
            <DialogDescription>
              {toTeam
                ? `A team KPI belongs to ${teamName ?? "the team"}: its value is the team's own, or its member seats' added up or averaged, and it is judged against the team's target.`
                : seat?.holderText
                  ? `A KPI belongs to the seat, ${seat.holderText}: whoever holds ${seat.seatTitle} carries it, and its history stays with the seat.`
                  : `A KPI belongs to the seat: whoever holds ${seat?.seatTitle ?? "it"} carries it, and its history stays with the seat.`}
            </DialogDescription>
          </DialogHeader>

          {pickable ? (
            <div className="flex flex-wrap items-center gap-3" data-slot="assign-to">
              <Segmented
                label="Assign to"
                value={assignTo}
                options={[
                  { value: "seat", label: `Seat${seat ? `: ${seat.seatTitle}` : ""}` },
                  { value: "team", label: "Team" },
                ]}
                onChange={(v) => setAssignTo(v)}
              />
              {toTeam ? (
                <NativeSelect
                  aria-label="Team"
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                >
                  <NativeSelectOption value="">
                    {teams.isPending ? "Loading teams…" : "Pick a team"}
                  </NativeSelectOption>
                  {editableTeams.map((x) => (
                    <NativeSelectOption key={x.id} value={x.id}>
                      {x.name}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              ) : null}
            </div>
          ) : toTeam ? (
            <p className="text-sm text-muted-foreground" data-slot="assign-to">
              Assign to: <span className="font-medium text-foreground">Team · {teamName}</span>
            </p>
          ) : null}

          <Step n={1} title="Measurable">
            {editing ? (
              <MetricSummary def={editDef} agent={agent} />
            ) : created ? (
              <MetricSummary def={created} agent={agent} />
            ) : (
              <>
                <Segmented
                  label="Measurable"
                  value={mode}
                  options={[
                    { value: "pick", label: "Pick a metric" },
                    { value: "create", label: "Create a new metric" },
                  ]}
                  onChange={(v) => setMode(v)}
                />
                {mode === "pick" ? (
                  <div className="flex flex-col gap-2">
                    <div className="relative">
                      <SearchIcon
                        aria-hidden="true"
                        className="pointer-events-none absolute top-2.5 left-2.5 size-4 text-muted-foreground"
                      />
                      <Input
                        aria-label="Find a metric"
                        placeholder="Find a metric by name…"
                        className="pl-8"
                        value={find}
                        onChange={(e) => setFind(e.target.value)}
                      />
                    </div>
                    <div
                      role="listbox"
                      aria-label="Metrics"
                      className="flex max-h-56 flex-col overflow-y-auto rounded-lg border border-border"
                      data-slot="metric-picker"
                    >
                      {shown.length ? (
                        shown.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            role="option"
                            aria-selected={picked === m.id}
                            onClick={() => {
                              setPicked(m.id);
                              setDirection(null);
                              setCadence(null);
                              setPeriod(null);
                            }}
                            className={cn(
                              "flex items-center gap-3 border-b border-border px-3 py-2 text-left last:border-b-0 hover:bg-accent focus-visible:bg-accent focus-visible:outline-none",
                              picked === m.id && "bg-accent",
                            )}
                          >
                            <span className="flex min-w-0 flex-1 flex-col">
                              <span className="truncate text-sm font-medium">{m.name}</span>
                              <span className="truncate text-xs text-muted-foreground">
                                {sourceText(m, agent)} · {labelOf(UNIT_OPTIONS, m.unit)} ·{" "}
                                {m.cadence ? labelOf(CADENCE_OPTIONS, m.cadence) : "No cadence"}
                              </span>
                            </span>
                            {picked === m.id ? (
                              <CheckIcon aria-hidden="true" className="size-4 text-primary" />
                            ) : null}
                          </button>
                        ))
                      ) : (
                        <p className="px-3 py-3 text-sm text-muted-foreground">
                          {metrics.isPending
                            ? "Loading metrics…"
                            : q
                              ? `No metric matches "${find.trim()}".`
                              : toTeam
                                ? "Every metric with a cadence is on this team already."
                                : "Every metric is on this seat already."}
                          {metrics.isPending ? null : (
                            <>
                              {" "}
                              <button
                                type="button"
                                className="text-primary underline-offset-2 hover:underline"
                                onClick={() => {
                                  setMode("create");
                                  if (q) setName(find.trim());
                                }}
                              >
                                Create one
                              </button>
                            </>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
                      <Field>
                        <FieldLabel htmlFor="kpi-new-name">Name</FieldLabel>
                        <Input
                          id="kpi-new-name"
                          value={name}
                          placeholder="Placements made"
                          onChange={(e) => setName(e.target.value)}
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="kpi-new-unit">Unit</FieldLabel>
                        <NativeSelect
                          id="kpi-new-unit"
                          value={unit}
                          onChange={(e) => setUnit(e.target.value as MetricDef["unit"])}
                        >
                          {UNIT_OPTIONS.map((o) => (
                            <NativeSelectOption key={o.value} value={o.value}>
                              {o.label}
                            </NativeSelectOption>
                          ))}
                        </NativeSelect>
                      </Field>
                    </div>
                    <Segmented
                      label="Where the numbers come from"
                      value={source}
                      options={[
                        { value: "manual", label: "Entered by hand" },
                        { value: "import", label: "CSV import" },
                        {
                          value: "event",
                          label: agent ? "Agent events" : "From events",
                        },
                      ]}
                      onChange={(v) => setSource(v)}
                    />
                    {source === "event" ? (
                      <EventQueryBuilder value={query} onChange={setQuery} />
                    ) : null}
                  </div>
                )}
              </>
            )}
          </Step>

          <Step n={2} title="How it is judged">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="kpi-direction">Good when</FieldLabel>
                <NativeSelect
                  id="kpi-direction"
                  value={dirNow}
                  onChange={(e) => setDirection(e.target.value as MetricDef["direction"])}
                >
                  {DIRECTION_OPTIONS.map((o) => (
                    <NativeSelectOption key={o.value} value={o.value}>
                      {o.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
              <Field>
                <FieldLabel htmlFor="kpi-cadence">Cadence</FieldLabel>
                <NativeSelect
                  id="kpi-cadence"
                  value={cadNow}
                  onChange={(e) => setCadence(e.target.value as NonNullable<MetricDef["cadence"]>)}
                >
                  {CADENCE_OPTIONS.map((o) => (
                    <NativeSelectOption key={o.value} value={o.value}>
                      {o.label}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
            </div>
            {defChanged ? (
              <p className="text-xs text-warning-text" data-slot="metric-change-note">
                Direction and cadence belong to {def?.name}: the change applies on every seat that
                uses it.
              </p>
            ) : null}
          </Step>

          <Step n={3} title={toTeam ? "Target for this team" : "Target for this seat"}>
            <div
              className={cn(
                "grid gap-4",
                dirNow === "between" ? "sm:grid-cols-3" : "sm:grid-cols-2",
              )}
            >
              <Field data-invalid={targetNow.trim() && storedTarget === null ? true : undefined}>
                <FieldLabel htmlFor="kpi-target">
                  {dirNow === "between" ? "From" : "Target"}
                </FieldLabel>
                <Input
                  id="kpi-target"
                  inputMode="decimal"
                  value={targetNow}
                  placeholder="None yet"
                  onChange={(e) => setTargetText(e.target.value)}
                />
              </Field>
              {dirNow === "between" ? (
                <Field>
                  <FieldLabel htmlFor="kpi-target-max">To</FieldLabel>
                  <Input
                    id="kpi-target-max"
                    inputMode="decimal"
                    value={maxNow}
                    onChange={(e) => setMaxText(e.target.value)}
                  />
                </Field>
              ) : null}
              <Field>
                <FieldLabel htmlFor="kpi-period">Per</FieldLabel>
                <NativeSelect
                  id="kpi-period"
                  value={periodNow}
                  onChange={(e) => setPeriod(e.target.value as Period)}
                >
                  {PERIOD_OPTIONS.filter((o) => o.value !== "day").map((o) => (
                    <NativeSelectOption key={o.value} value={o.value}>
                      {o.label.replace("Per ", "")}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              </Field>
            </div>
            <FieldDescription>
              {UNIT_HINT[unitNow] ? `${UNIT_HINT[unitNow]} ` : ""}
              Empty: the KPI shows its numbers, but it is not judged until it has a target.
            </FieldDescription>
          </Step>

          {toTeam ? (
            <Step n={4} title="Where the value comes from">
              <Segmented
                label="Value comes from"
                value={rollup}
                options={ROLLUPS.map((r) => ({ value: r, label: TEAM_ROLLUP_LABELS[r] }))}
                onChange={(v) => setRollup(v)}
              />
              <p className="text-xs text-muted-foreground" data-slot="rollup-hint">
                {rollup === "none"
                  ? def?.source === "event"
                    ? "The team's value is its member seats' events, added up."
                    : "The team's own value: its lead or a manager enters or imports it."
                  : rollup === "sum"
                    ? "The member seats' values for the period, added up. A seat without a value is left out, never counted as zero."
                    : "The average of the member seats' values for the period. For a rate, prefer a formula metric: a ratio of totals, not an average of ratios."}
              </p>
              <Field data-invalid={WEIGHT.test(kpiWeight) ? undefined : true} className="max-w-40">
                <FieldLabel htmlFor="kpi-weight">KPI weight</FieldLabel>
                <Input
                  id="kpi-weight"
                  inputMode="decimal"
                  value={kpiWeight}
                  onChange={(e) => setKpiWeight(e.target.value)}
                />
              </Field>
            </Step>
          ) : (
            <Step n={4} title="Scoring">
              <Segmented
                label="Tracking"
                value={tracking}
                options={TRACKING.map((o) => ({ value: o.value, label: o.label }))}
                onChange={(v) => setTracking(v)}
              />
              <p className="text-xs text-muted-foreground">
                {TRACKING.find((o) => o.value === tracking)?.hint}
              </p>
              <div className="grid items-end gap-4 sm:grid-cols-3">
                <Field data-invalid={WEIGHT.test(kpiWeight) ? undefined : true}>
                  <FieldLabel htmlFor="kpi-weight">KPI weight</FieldLabel>
                  <Input
                    id="kpi-weight"
                    inputMode="decimal"
                    value={kpiWeight}
                    disabled={tracking !== "scored"}
                    onChange={(e) => setKpiWeight(e.target.value)}
                  />
                </Field>
                <Field data-invalid={WEIGHT.test(goalWeight) ? undefined : true}>
                  <FieldLabel htmlFor="kpi-goal-weight">Goal weight</FieldLabel>
                  <Input
                    id="kpi-goal-weight"
                    inputMode="decimal"
                    value={goalWeight}
                    disabled={tracking === "off"}
                    onChange={(e) => setGoalWeight(e.target.value)}
                  />
                </Field>
                <label
                  htmlFor="kpi-penalty"
                  className="flex h-9 items-center gap-2 text-sm"
                  data-slot="penalty-only"
                >
                  <Checkbox
                    id="kpi-penalty"
                    checked={penalty}
                    disabled={tracking !== "scored"}
                    onCheckedChange={(v) => setPenalty(v === true)}
                  />
                  Penalty only
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Penalty only: a miss pulls the score down, a hit adds nothing.
              </p>
            </Step>
          )}

          <Step n={5} title="Warning bands">
            <BandsEditor
              value={bandsNow}
              onChange={setBandsForm}
              direction={dirNow}
              target={storedTarget}
              unit={unitNow}
              currency={currency}
              metricName={creating ? name.trim() || null : (def?.name ?? null)}
            />
          </Step>

          {save.error instanceof ApiError && save.error.status === 409 ? (
            <p role="alert" className="text-sm text-destructive">
              This seat's KPIs changed since you opened them. They are reloaded: save again to apply
              your change over them.
            </p>
          ) : (
            <FormError what="Saving the KPI" error={save.error} />
          )}
          <DialogFooter className="items-center">
            {problems[0] ? (
              <span className="mr-auto text-xs text-muted-foreground" role="status">
                {problems[0]}
              </span>
            ) : null}
            <Button type="button" variant="secondary" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={problems.length > 0 || save.isPending}>
              {save.isPending ? "Saving…" : editing || editingTeam ? "Save KPI" : "Add KPI"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Ends a seat target from `from` (a rule that starts then or later is removed instead). */
async function endTarget(api: Api, co: string, rule: TargetRule, from: string) {
  if (rule.effective_from >= from) {
    await api.client.targets.delete({ params: { co, id: rule.id } });
    return;
  }
  const day = new Date(`${from}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() - 1);
  await api.client.targets.update({
    params: { co, id: rule.id },
    body: { effective_to: day.toISOString().slice(0, 10) } as never,
  });
}

/** Where a metric's numbers come from, in the holder's terms (an agent's events read "agent"). */
export function sourceText(m: Pick<MetricDef, "source">, agent?: boolean | undefined): string {
  if (m.source === "event") return agent ? "Agent events" : "Events";
  return {
    manual: "Manual",
    import: "Import",
    formula: "Formula",
    ledger: "Cost ledger",
    task: "Board tasks",
  }[m.source];
}

function MetricSummary({
  def,
  agent,
}: {
  def: MetricDef | undefined;
  agent?: boolean | undefined;
}) {
  if (!def) return <p className="text-sm text-muted-foreground">Loading the metric…</p>;
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-border px-3 py-2">
      <span className="text-sm font-medium">{def.name}</span>
      <span className="text-xs text-muted-foreground">
        {sourceText(def, agent)} · {labelOf(UNIT_OPTIONS, def.unit)}
        {def.event_query ? ` · ${describeQuery(def.event_query)}` : ""}
      </span>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3" aria-labelledby={`kpi-step-${n}`}>
      <h3 id={`kpi-step-${n}`} className="flex items-center gap-2 text-sm font-semibold">
        <span
          aria-hidden="true"
          className="inline-flex size-5 items-center justify-center rounded-full bg-muted text-xs text-muted-foreground"
        >
          {n}
        </span>
        {title}
      </h3>
      {children}
    </section>
  );
}

/** A row of mutually exclusive choices (buttons with aria-pressed, in a labelled group). */
function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <fieldset
      aria-label={label}
      className="m-0 inline-flex w-fit min-w-0 flex-wrap gap-1 rounded-lg border-0 bg-muted p-1"
    >
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-md px-3 py-1 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-ring",
            value === o.value
              ? "bg-background font-medium text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </fieldset>
  );
}
